import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import preferencesAPI from '@/api/preferences'; 
import { useRightDrawerStore } from "@/store/rightDrawer";
import type { CardField } from '@/types/cards';

interface ColumnPreference {
  title: string;
  value: string;
  format: string;
  sortable: boolean;
}

type PreferencesState = Record<
  string, // moduleName
  { 
    headers?: { data: any[] },
    cards?: { data: any[] }
  }
>;

export const usePreferencesStore = defineStore('preferences', () => {
  const preferences = ref<PreferencesState>({});

  // === TABLE ===
  const allModuleHeaders = ref<Record<string, ColumnPreference[]>>({}); // headers por módulo
  const defaultModuleHeaders = ref<Record<string, ColumnPreference[]>>({});

  // === CARDS (MOBILE) === 👇
  const allModuleCardFields = ref<Record<string, CardField[]>>({});
  const defaultModuleCardFields = ref<Record<string, CardField[]>>({});

  const currentModule = ref<string>(''); 
  const rightDrawerStore = useRightDrawerStore();

  // === GETTERS ===
  const userPreferenceModuleHeaders = computed(() => {
    return preferences.value?.[currentModule.value]?.headers?.data || [];
  });
  const userPreferenceModuleCards = computed(() => {
    return preferences.value?.[currentModule.value]?.cards?.data || [];
  });

  const getAllHeadersByModule = (module: string) => allModuleHeaders.value[module] || [];
  const getDefaultHeadersByModule = (module: string) => defaultModuleHeaders.value[module] || [];

  const getAllCardFieldsByModule = (module: string) => allModuleCardFields.value[module] || [];
  const getDefaultCardFieldsByModule = (module: string) => defaultModuleCardFields.value[module] || [];

  // === MUTATORS ===

  const mergeMissingDefaultHeaders = (currentHeaders: any[], defaultHeaders: any[]) => {
    const result = [...currentHeaders];
    const hasHeader = (value: string) => result.some((header: any) => header.value === value);

    defaultHeaders.forEach((defaultHeader: any, defaultIndex: number) => {
      if (!defaultHeader?.value || hasHeader(defaultHeader.value)) return;

      const previousDefaultValue = defaultHeaders
        .slice(0, defaultIndex)
        .reverse()
        .find((header: any) => hasHeader(header.value))?.value;

      if (previousDefaultValue) {
        const previousIndex = result.findIndex((header: any) => header.value === previousDefaultValue);
        result.splice(previousIndex + 1, 0, { ...defaultHeader });
        return;
      }

      const nextDefaultValue = defaultHeaders
        .slice(defaultIndex + 1)
        .find((header: any) => hasHeader(header.value))?.value;

      if (nextDefaultValue) {
        const nextIndex = result.findIndex((header: any) => header.value === nextDefaultValue);
        result.splice(Math.max(nextIndex, 0), 0, { ...defaultHeader });
        return;
      }

      result.push({ ...defaultHeader });
    });

    return result;
  };

  const hydrateHeadersWithNewDefaults = (module: string, currentHeaders: any[], defaultHeaders: any[]) => {
    if (!module || !currentHeaders.length || !defaultHeaders.length) {
      return currentHeaders;
    }

    const signature = defaultHeaders.map((header: any) => header.value).join('|');
    const signaturesKey = 'preferences_default_header_signatures';
    const signatures = JSON.parse(localStorage.getItem(signaturesKey) || '{}');

    if (signatures[module] === signature) {
      return currentHeaders;
    }

    const mergedHeaders = mergeMissingDefaultHeaders(currentHeaders, defaultHeaders);
    signatures[module] = signature;
    localStorage.setItem(signaturesKey, JSON.stringify(signatures));

    const savedValues = preferences.value?.[module]?.headers?.data || [];
    if (savedValues.length) {
      preferences.value = {
        ...preferences.value,
        [module]: {
          ...(preferences.value[module] || {}),
          headers: {
            ...(preferences.value[module]?.headers || {}),
            data: mergedHeaders.map((header: any) => header.value),
          },
        },
      };
      localStorage.setItem('preferences', JSON.stringify(preferences.value));
    }

    return mergedHeaders;
  };

  // Guarda preferencias en memoria y localStorage
  const setPreferences = (newPreferences: PreferencesState) => {
    preferences.value = newPreferences;
    localStorage.setItem('preferences', JSON.stringify(preferences.value));
  };

  // Carga preferencias de localStorage
  const loadPreferences = () => {
    const stored = localStorage.getItem('preferences');
    if (stored) {
      preferences.value = JSON.parse(stored);
    }
  };

  // Guarda preferencias (headers) para un módulo
  const savePreferences = async (headers: any[], module: string) => {
    if (!module) return;
    const payload: any = [];
    if (headers && headers.length > 0) {
      const headersArray = [
        ['module', module],
        ['subject', 'headers'],
        ['data', headers]
      ];
      payload.push(Object.fromEntries(headersArray));
    }
    try {
      const response = await preferencesAPI.createPreference(payload);
      if (response.data) {
        preferences.value = response.data;
        localStorage.setItem('preferences', JSON.stringify(preferences.value));
        rightDrawerStore.closeDrawer();
      } else {
        console.error("Failed to save preferences:", response);
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  const saveCardPreferences = async (cards: any[], module: string) => {
    if (!module) return;
    const payload = [{ module, subject: 'cards', data: cards }];
    try {
      const response = await preferencesAPI.createPreference(payload);
      if (response.data) {
        preferences.value = response.data;
        localStorage.setItem('preferences', JSON.stringify(preferences.value));
        rightDrawerStore.closeDrawer();
      } else {
        console.error('Failed to save card preferences:', response);
      }
    } catch (error) {
      console.error('Error saving card preferences:', error);
    }
  };

  // Set headers para cada módulo
  const setAllModuleHeaders = (module: string, headers: ColumnPreference[]) => {
    allModuleHeaders.value[module] = headers;
  };

  const setDefaultModuleHeaders = (module: string, headers: ColumnPreference[]) => {
    defaultModuleHeaders.value[module] = headers;
  };

  const setAllModuleCardFields = (module: string, fields: CardField[]) => {
    allModuleCardFields.value[module] = fields;
  };
  const setDefaultModuleCardFields = (module: string, fields: CardField[]) => {
    defaultModuleCardFields.value[module] = fields;
  };

  return {
    preferences,
    setPreferences,
    loadPreferences,
    savePreferences,
    saveCardPreferences,
    allModuleHeaders,
    defaultModuleHeaders,
    userPreferenceModuleHeaders,
    setAllModuleHeaders,
    setAllModuleCardFields,
    setDefaultModuleHeaders,
    setDefaultModuleCardFields,
    hydrateHeadersWithNewDefaults,
    currentModule,
    getAllHeadersByModule,
    getDefaultHeadersByModule,
    getAllCardFieldsByModule,
    getDefaultCardFieldsByModule
  };
});
